/**
 * KgRelatedSidebar — small card that shows a few related KG entities on
 * an entity detail page so readers keep exploring instead of hitting a
 * dead-end. Powered by `GET /api/kg/entities/{slug}/related`.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  MapPin, Users, Calendar, Building2, Stethoscope, ArrowRight, Loader2,
} from "lucide-react";
import { http } from "@/lib/api";

const TYPE_ICON = {
  place:        MapPin,
  person:       Users,
  organization: Building2,
  event:        Calendar,
  disease:      Stethoscope,
};
const TYPE_PREFIX = {
  place: "/place", person: "/person", organization: "/organization",
  event: "/event", disease: "/disease",
};

export default function KgRelatedSidebar({ slug, entityType, limit = 6 }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await http.get(`/kg/entities/${encodeURIComponent(slug)}/related?limit=${limit}`);
        if (alive) setItems(data.items || []);
      } catch {
        if (alive) setItems([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [slug, limit]);

  if (loading) {
    return (
      <div className="wofy-card" data-testid="kg-related-loading">
        <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>KEEP EXPLORING</div>
        <div className="flex items-center gap-2 text-sm opacity-70">
          <Loader2 className="w-4 h-4 animate-spin" /> Finding related entities…
        </div>
      </div>
    );
  }
  if (!items || items.length === 0) return null;

  return (
    <aside className="wofy-card" data-testid="kg-related-sidebar">
      <div className="overline mb-3" style={{ color: "var(--wofy-primary)" }}>KEEP EXPLORING</div>
      <ul className="space-y-3">
        {items.map((it) => {
          const Icon = TYPE_ICON[it.entity_type] || Users;
          const prefix = TYPE_PREFIX[it.entity_type] || "/entity";
          return (
            <li key={it.slug}>
              <Link
                to={`${prefix}/${it.slug}`}
                data-testid={`kg-related-${it.slug}`}
                className="group block rounded-lg border p-3 transition hover:bg-black/5"
                style={{ borderColor: "var(--wofy-border)" }}
              >
                <div className="flex items-start gap-2">
                  <div className="mt-0.5 rounded-full p-1.5 shrink-0"
                       style={{ background: "var(--wofy-primary-soft, rgba(178,93,56,0.08))" }}>
                    <Icon className="w-3.5 h-3.5" style={{ color: "var(--wofy-primary)" }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium leading-tight truncate group-hover:underline">
                      {it.name}
                    </div>
                    <div className="text-[11px] font-mono uppercase tracking-wider opacity-60 mt-0.5">
                      {it.reason}
                    </div>
                    {it.short && (
                      <p className="text-xs opacity-75 mt-1 line-clamp-2">{it.short}</p>
                    )}
                  </div>
                  <ArrowRight className="w-3.5 h-3.5 opacity-40 group-hover:opacity-100 mt-1" />
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}
